<script lang="ts" setup>
import { AppButton, AppOTP } from '@/core/components';
import type { FormErrors } from '@/types/Form';
import { computed } from 'vue';
import type { ForgotPasswordVerifyForm } from '../../types/Auth';

const { validations } = useValidation();

const props = defineProps<{
  username: string;
}>();

const { form, handleSubmit, getErrors } = useForm<ForgotPasswordVerifyForm>({
  form: {
    username: props.username,
    token: '',
  },
  rules: {
    token: {
      required: validations.required('OTP'),
      minLength: validations.minLength('OTP', 5),
    },
  },
});

const {
  data,
  pending: loadingVerify,
  execute: execVerify,
  error: serverError,
} = useSubmit<{ data: void }, { data: FormErrors<ForgotPasswordVerifyForm> }>();
loadingVerify.value = false;

const emits = defineEmits<{
  (e: 'next', token: string): void;
}>();

const handleSignUp = async (): Promise<void> => {
  handleSubmit(async () => {
    await execVerify('/auth/reset-password/check?X-NO-AUTH=true', {
      method: 'POST',
      body: form.value,
    });

    if (!serverError.value) {
      emits('next', form.value.token);
    }
  });
};

const isResendSuccess = ref(false);

const { error: serverResendError, execute: execSubmit, pending } = useSubmit();
const handleResendOtp = async (): Promise<void> => {
  await execSubmit('/auth/forgot-password', {
    method: 'POST',
    body: { username: form.value.username },
  });

  if (!serverResendError.value) {
    isResendSuccess.value = true;
    setTimeout(() => {
      isResendSuccess.value = false;
    }, 500);
  }
};

const errors = computed(() => {
  return getErrors({ token: serverError.value?.data?.errors?.token });
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <AppOTP
      v-model="form.token"
      :disabled="loadingVerify"
      :errors="errors.token"
      data-testid="otp-input"
      @resend="handleResendOtp"
      :is-resend-success="isResendSuccess"
      :loading="pending"
    />

    <AppButton block data-testid="continue" data-cy="continue" class="mt-4 font-weight-bold" :loading="loadingVerify">
      Continue
    </AppButton>
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
